import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  frappePost,
  clearAuthCredentials,
  setAuthCredentials,
  isAuthenticated,
  onSessionExpired,
  getStoredUser,
  validateSession,
} from '@/lib/frappe';
import type { User, UserRole, TeacherProfile, StudentProfile } from '@/types/api';

interface AuthState {
  user: User | null;
  profile: TeacherProfile | StudentProfile | null;
  isLoading: boolean;
  isLoggedIn: boolean;
  role: UserRole;
  isSessionValid: boolean;
}

interface LoginResponse {
  success: boolean;
  message?: string;
  user?: User;
  sid?: string;
  api_key?: string;
  api_secret?: string;
}

interface CurrentUserResponse {
  success: boolean;
  is_logged_in: boolean;
  user?: {
    email: string;
    full_name: string;
    first_name: string;
    last_name: string;
    user_image?: string;
    role: UserRole;
    profile?: TeacherProfile | StudentProfile;
  };
}

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<{ success: boolean; message?: string }>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  updateProfile: (profile: Partial<TeacherProfile | StudentProfile>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Session validation interval (5 minutes)
const SESSION_VALIDATION_INTERVAL = 5 * 60 * 1000;

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    profile: null,
    isLoading: true,
    isLoggedIn: false,
    role: 'guest',
    isSessionValid: false,
  });

  const validationIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isInitialized = useRef(false);

  // Initialize from stored user data for instant UI feedback
  const initializeFromStorage = useCallback(() => {
    const storedUser = getStoredUser();
    if (storedUser && isAuthenticated()) {
      setState(prev => ({
        ...prev,
        user: {
          email: storedUser.email,
          full_name: storedUser.full_name,
          first_name: '',
          last_name: '',
          role: storedUser.role as UserRole,
        },
        isLoggedIn: true,
        role: storedUser.role as UserRole,
        isSessionValid: true, // Assume valid until validated
      }));
    }
  }, []);

  // Fetch current user from server
  const refreshUser = useCallback(async () => {
    if (!isAuthenticated()) {
      setState({
        user: null,
        profile: null,
        isLoading: false,
        isLoggedIn: false,
        role: 'guest',
        isSessionValid: false,
      });
      return;
    }

    try {
      const response = await frappePost<CurrentUserResponse>(
        'hudan_lms.api.auth.get_current_user',
        {},
        true
      );

      if (response.success && response.is_logged_in && response.user) {
        const user: User = {
          email: response.user.email,
          full_name: response.user.full_name,
          first_name: response.user.first_name,
          last_name: response.user.last_name,
          user_image: response.user.user_image,
          role: response.user.role,
        };

        // Update stored user data
        setAuthCredentials('', '', {
          email: user.email,
          full_name: user.full_name,
          role: user.role,
        });

        setState({
          user,
          profile: response.user.profile || null,
          isLoading: false,
          isLoggedIn: true,
          role: response.user.role,
          isSessionValid: true,
        });
      } else {
        clearAuthCredentials();
        setState({
          user: null,
          profile: null,
          isLoading: false,
          isLoggedIn: false,
          role: 'guest',
          isSessionValid: false,
        });
      }
    } catch (error) {
      console.error('Failed to fetch current user:', error);
      clearAuthCredentials();
      setState({
        user: null,
        profile: null,
        isLoading: false,
        isLoggedIn: false,
        role: 'guest',
        isSessionValid: false,
      });
    }
  }, []);

  // Periodic session validation
  const startSessionValidation = useCallback(() => {
    // Clear existing interval
    if (validationIntervalRef.current) {
      clearInterval(validationIntervalRef.current);
    }

    validationIntervalRef.current = setInterval(async () => {
      if (isAuthenticated()) {
        const isValid = await validateSession();
        if (!isValid) {
          setState(prev => ({
            ...prev,
            isSessionValid: false,
          }));
        }
      }
    }, SESSION_VALIDATION_INTERVAL);
  }, []);

  const stopSessionValidation = useCallback(() => {
    if (validationIntervalRef.current) {
      clearInterval(validationIntervalRef.current);
      validationIntervalRef.current = null;
    }
  }, []);

  // Initialize auth state
  useEffect(() => {
    if (isInitialized.current) return;
    isInitialized.current = true;

    // Quick initialization from storage
    initializeFromStorage();

    // Then validate with server
    refreshUser();

    // Start periodic validation
    startSessionValidation();

    return () => {
      stopSessionValidation();
    };
  }, [initializeFromStorage, refreshUser, startSessionValidation, stopSessionValidation]);

  // Listen for session expired events
  useEffect(() => {
    const cleanup = onSessionExpired(() => {
      setState({
        user: null,
        profile: null,
        isLoading: false,
        isLoggedIn: false,
        role: 'guest',
        isSessionValid: false,
      });
      stopSessionValidation();
    });

    return cleanup;
  }, [stopSessionValidation]);

  // Login function
  const login = async (email: string, password: string): Promise<{ success: boolean; message?: string }> => {
    try {
      setState(prev => ({ ...prev, isLoading: true }));

      const response = await frappePost<LoginResponse>(
        'hudan_lms.api.auth.login',
        { email, password },
        false
      );

      if (response.success && response.user) {
        // Store credentials
        const apiKeyFull = response.api_key && response.api_secret
          ? `${response.api_key}:${response.api_secret}`
          : '';

        setAuthCredentials(apiKeyFull, response.sid || '', {
          email: response.user.email,
          full_name: response.user.full_name,
          role: response.user.role,
        });

        // Fetch full user profile
        await refreshUser();

        // Start session validation
        startSessionValidation();

        return { success: true };
      }

      setState(prev => ({ ...prev, isLoading: false }));
      return {
        success: false,
        message: response.message || 'Login failed',
      };
    } catch (error) {
      setState(prev => ({ ...prev, isLoading: false }));
      return {
        success: false,
        message: error instanceof Error ? error.message : 'An error occurred during login',
      };
    }
  };

  // Logout function
  const logout = async () => {
    try {
      await frappePost('hudan_lms.api.auth.logout', {}, true);
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearAuthCredentials();
      stopSessionValidation();
      setState({
        user: null,
        profile: null,
        isLoading: false,
        isLoggedIn: false,
        role: 'guest',
        isSessionValid: false,
      });
    }
  };

  // Update profile locally (after successful API update)
  const updateProfile = (profile: Partial<TeacherProfile | StudentProfile>) => {
    setState(prev => ({
      ...prev,
      profile: prev.profile ? { ...prev.profile, ...profile } : null,
    }));
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        logout,
        refreshUser,
        updateProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Hook for requiring authentication
export function useRequireAuth(redirectTo: string = '/login') {
  const { isLoggedIn, isLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && !isLoggedIn) {
      navigate(redirectTo, { replace: true });
    }
  }, [isLoggedIn, isLoading, navigate, redirectTo]);

  return { isLoggedIn, isLoading };
}

// Hook for redirecting authenticated users
export function useRedirectIfAuthenticated(redirectTo: string = '/') {
  const { isLoggedIn, isLoading, role } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && isLoggedIn) {
      // Redirect based on role
      const roleRedirects: Record<UserRole, string> = {
        admin: '/admin',
        teacher: '/teacher',
        student: '/student',
        parent: '/family',
        guest: redirectTo,
      };
      navigate(roleRedirects[role] || redirectTo, { replace: true });
    }
  }, [isLoggedIn, isLoading, role, navigate, redirectTo]);

  return { isLoggedIn, isLoading };
}
